import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MaterialReactTable,
  useMaterialReactTable,
  createMRTColumnHelper,
  type MRT_ColumnDef,
} from 'material-react-table';

import {
  Box,
  Button,
  ListItemIcon,
  MenuItem,
  Typography,
  lighten,
} from '@mui/material';

import { LoanAmortization } from '../../../interface/ILoanManagement';

import helper from '../../../helpers/index';

import iconPaid from '../../../assets/success-icon.png';
import iconView from '../../../assets/visible.png';
import iconEdit from '../../../assets/edit-icon.png';
import iconDelete from '../../../assets/delete.png';



//example data type


const columnHelper = createMRTColumnHelper<LoanAmortization>();

interface LoanAmortizationTableProps {
  data: LoanAmortization[];
}



const LoanAmortizationTable: React.FC<LoanAmortizationTableProps> = ({ data }) => {
  const navigate = useNavigate();

  //should be memoized or stable
  const columns = useMemo<MRT_ColumnDef<LoanAmortization>[]>(
    () => [
      {
        accessorKey: 'loanTermAmortizationId',
        header: 'Payment No.',
        enableSorting: true
      },
      {
        accessorFn: (row: LoanAmortization) => row.dueDate ? `${helper.formatLongDate(row.dueDate)}` : '',
        id: 'dueDate',
        header: 'Due Date',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const firstData = new Date(rowA.original.dueDate || new Date()).getTime() || 0;
          const secondData = new Date(rowB.original.dueDate || new Date()).getTime() || 0;
          // Perform numeric comparison
          return Number(firstData) - Number(secondData);
        }
      },
      {
        accessorKey: 'periodicPayments',
        header: 'Periodic Payments',
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.periodicPayments;
          const idB = rowB.original.periodicPayments;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        accessorKey: 'principalRepaid',
        header: 'Principal Repaid',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.principalRepaid;
          const idB = rowB.original.principalRepaid;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        accessorKey: 'interestPaid',
        header: 'Intereset Paid',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.interestPaid;
          const idB = rowB.original.interestPaid;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        accessorKey: 'unpaidBalance',
        header: 'Unpaid Balance',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.unpaidBalance;
          const idB = rowB.original.unpaidBalance;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        header: 'Status',
        accessorFn: (row: LoanAmortization) => {
          // Initialize local state for each row's payment status

          return (String(row.isPaid) === 'true') ? (<span style={{ color: 'green', fontWeight: 'bold' }}>Paid</span>) : (<span style={{ color: 'red', fontWeight: 'bold' }}>Unpaid</span>);

        },
      }
      ,
      {
        header: 'Action',
        accessorFn: (row: LoanAmortization) => {
          return (
            <Box>
              {String(row.isPaid) === 'true' ? (
                <img className='responsive-image action-icon' src={iconPaid} />
              ) : (
                <button
                  type="button"
                  className='btn-green-2 center-middle'
                  onClick={() => navigate(`/Members/Payment/${row.loanTermAmortizationId}`, { state: { loanTermAmortizationId: row.loanTermAmortizationId } })}
                >
                  Pay Now
                </button>
              )}
            </Box>
          );
        }
      }
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    muiTablePaperProps: {
      elevation: 1,
      sx: {
        boxShadow: 'none',
        width: '100%',
        background: 'transparent',
        display: 'grid',

      }
    },
    enableColumnActions: false,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.index % 2 === 0 ? 'transparent' : '#E7EFEA',
      },
    }),
  });

  return <MaterialReactTable table={table} />;
};

export default LoanAmortizationTable;
